// src/components/MapView/GeographyDetails.jsx
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  X,
  Trophy,
  MapPin,
  TrendingUp,
  Info,
  Users,
  ExternalLink,
} from "lucide-react";

import {
  useGeographyDetails,
  useDisplayConfig,
  useValueFormatter,
  useTierConfig,
  useFeatures,
} from "../../hooks";
import { clearSelectedGeography } from "../../store/mapSlice";
import {
  selectActiveCompetitors,
  selectActiveCompetitorData,
  selectCompetitorsForGeography,
  selectCompetitorsLoading,
  fetchCompetitorData,
} from "../../store/dataSlice";
import {
  selectActiveDataPaths,
  selectActiveGeographyType,
} from "../../store/configSlice";

/**
 * Score breakdown bar component
 */
const ScoreBar = ({ label, score, maxScore, tier, rawValue, color }) => {
  const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0;

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-gray-600 font-medium">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">
            {typeof rawValue === "number" ? rawValue.toFixed(1) : rawValue}
          </span>
          <span className="font-semibold text-gray-900">
            {score.toFixed(1)} / {maxScore}
          </span>
        </div>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{
            width: `${percentage}%`,
            backgroundColor: color || "#15803d",
          }}
        />
      </div>
      <div className="text-xs text-gray-400 mt-0.5">{tier}</div>
    </div>
  );
};

/**
 * GeographyDetails component - shows details for a selected geography
 */
const GeographyDetails = ({ geographyId }) => {
  const dispatch = useDispatch();
  const details = useGeographyDetails(geographyId);
  console.log(details);
  const { detailsConfig, geographyLabels } = useDisplayConfig();
  const { formatValue } = useValueFormatter();
  const { getTierColor } = useTierConfig();
  const { competitorsEnabled } = useFeatures();
  const dataPaths = useSelector(selectActiveDataPaths);
  const competitorData = useSelector(selectActiveCompetitorData);
  const competitorsLoading = useSelector(selectCompetitorsLoading);
  const competitorsForGeo = useSelector((state) =>
    selectCompetitorsForGeography(state, geographyId),
  );

  const [showAllCompetitors, setShowAllCompetitors] = React.useState(false);

  // Reset "show all" when geography changes
  React.useEffect(() => {
    setShowAllCompetitors(false);
  }, [geographyId]);

  // Auto-fetch competitor data when a geography is selected and competitors
  // are enabled, even if the map toggle is off. Data is cached by path so
  // this is a no-op after the first load.
  React.useEffect(() => {
    if (
      competitorsEnabled &&
      dataPaths?.competitors &&
      competitorData === null &&
      !competitorsLoading
    ) {
      dispatch(fetchCompetitorData());
    }
  }, [
    competitorsEnabled,
    dataPaths?.competitors,
    competitorData,
    competitorsLoading,
    dispatch,
  ]);

  // Show competitor section whenever competitors are enabled and this map has a competitors path
  const showCompetitorSection = competitorsEnabled && !!dataPaths?.competitors;

  if (!details) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 text-center">
        <p className="text-gray-500 text-sm">Geography not found</p>
      </div>
    );
  }

  const handleClose = () => {
    dispatch(clearSelectedGeography());
  };

  return (
    <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
      {/* Header */}
      <div
        className="px-4 py-3 flex items-start justify-between"
        style={{ backgroundColor: `${details.tierColor}20` }}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-gray-500" />
            <h3 className="font-semibold text-gray-900">
              {details.label || "Unknown Location"}
            </h3>
          </div>
          {details.secondaryLabel && (
            <p className="text-sm text-gray-500 mt-0.5 ml-6">
              {geographyLabels.singular}: {details.secondaryLabel}
            </p>
          )}
        </div>
        <button
          onClick={handleClose}
          className="p-1 hover:bg-gray-200 rounded transition"
          aria-label="Close details"
        >
          <X size={16} className="text-gray-500" />
        </button>
      </div>

      {/* Score & Rank */}
      <div className="px-4 py-3 border-b bg-gray-50">
        <div className="flex items-center justify-between">
          {/* Score */}
          <div className="flex items-center gap-3">
            <div
              className="w-16 h-16 rounded-lg flex items-center justify-center shadow-sm"
              style={{ backgroundColor: details.tierColor }}
            >
              <span className="text-white font-bold text-xl">
                {details.score ?? "–"}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                Score
              </p>
              <p className="text-sm font-medium text-gray-700">
                {details.score !== null
                  ? `${details.score} / ${details.maxScore}`
                  : "N/A"}
              </p>
            </div>
          </div>

          {/* Rank */}
          <div className="text-right">
            <div className="flex items-center gap-1 justify-end">
              <Trophy size={14} className="text-yellow-500" />
              <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                Rank
              </span>
            </div>
            <p className="text-xl font-bold text-gray-900">
              #{details.rank ?? "–"}
              <span className="text-sm font-normal text-gray-500">
                {" "}
                / {details.totalCount.toLocaleString()}
              </span>
            </p>
          </div>
        </div>

        {/* Tier badge */}
        <div className="mt-3">
          <span
            className="inline-block px-3 py-1 rounded-full text-sm font-semibold text-white shadow-sm"
            style={{ backgroundColor: details.tierColor }}
          >
            {details.tierLabel}
          </span>
        </div>
      </div>

      {/* Score Breakdown */}
      {details.breakdown && details.breakdown.length > 0 && (
        <div className="px-4 py-3 border-b">
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <TrendingUp size={14} />
            Score Breakdown
          </h4>
          <div className="space-y-1">
            {details.breakdown.map((item, idx) => (
              <ScoreBar
                key={idx}
                label={item.variableName}
                score={item.weightedScore}
                maxScore={item.weight}
                tier={item.tier}
                rawValue={item.rawValue}
                color={getTierColor(item.tier)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Location Details */}
      {/* {details.raw && (
        <div className="px-4 py-3 border-b">
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <Info size={14} />
            Location Details
          </h4>
          <dl className="space-y-1">
            {details.raw.city && (
              <div className="flex items-center justify-between">
                <dt className="text-sm text-gray-500">City</dt>
                <dd className="text-sm font-medium text-gray-900">{details.raw.city}</dd>
              </div>
            )}
            {details.raw.county && (
              <div className="flex items-center justify-between">
                <dt className="text-sm text-gray-500">County/Region</dt>
                <dd className="text-sm font-medium text-gray-900">{details.raw.county}</dd>
              </div>
            )}
            {details.raw.state && (
              <div className="flex items-center justify-between">
                <dt className="text-sm text-gray-500">State</dt>
                <dd className="text-sm font-medium text-gray-900">{details.raw.state}</dd>
              </div>
            )}
          </dl>
        </div>
      )} */}
      {/* Location Details */}
      {details.raw && (
        <div className="px-4 py-3 border-b">
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <Info size={14} />
            Location Details
          </h4>
          <dl className="space-y-1">
            {details.raw.city && (
              <div className="flex items-start justify-between gap-4">
                <dt className="text-sm text-gray-500 flex-shrink-0">City</dt>
                <dd className="text-sm font-medium text-gray-900 text-right">
                  {details.raw.city}
                </dd>
              </div>
            )}
            {details.raw.county && (
              <div className="flex items-start justify-between gap-4">
                <dt className="text-sm text-gray-500 flex-shrink-0">County</dt>
                <dd className="text-sm font-medium text-gray-900 text-right">
                  {details.raw.county
                    .replace(/ Planning Region$/i, "")
                    .replace(/ Region$/i, "")}
                </dd>
              </div>
            )}
            {details.raw.state && (
              <div className="flex items-start justify-between gap-4">
                <dt className="text-sm text-gray-500 flex-shrink-0">State</dt>
                <dd className="text-sm font-medium text-gray-900 text-right">
                  {details.raw.state}
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {/* Demographics */}
      {details.raw && (
        <div className="px-4 py-3">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">
            Demographics
          </h4>
          <dl className="space-y-1">
            {/* Always show these base demographics */}
            {details.raw.population != null && (
              <div className="flex items-start justify-between gap-4">
                <dt className="text-sm text-gray-500 flex-shrink-0">
                  Population
                </dt>
                <dd className="text-sm font-medium text-gray-900 text-right">
                  {details.raw.population.toLocaleString()}
                </dd>
              </div>
            )}
            {details.raw.households != null && (
              <div className="flex items-start justify-between gap-4">
                <dt className="text-sm text-gray-500 flex-shrink-0">
                  Households
                </dt>
                <dd className="text-sm font-medium text-gray-900 text-right">
                  {details.raw.households.toLocaleString()}
                </dd>
              </div>
            )}
            {details.raw.medianHouseholdIncome != null && (
              <div className="flex items-start justify-between gap-4">
                <dt className="text-sm text-gray-500 flex-shrink-0">
                  Median Household Income
                </dt>
                <dd className="text-sm font-medium text-gray-900 text-right">
                  ${details.raw.medianHouseholdIncome.toLocaleString()}
                </dd>
              </div>
            )}
            {details.raw.populationDensity != null && (
              <div className="flex items-start justify-between gap-4">
                <dt className="text-sm text-gray-500 flex-shrink-0">
                  Population Density
                </dt>
                <dd className="text-sm font-medium text-gray-900 text-right">
                  {details.raw.populationDensity.toLocaleString()}/sq mi
                </dd>
              </div>
            )}
            {details.raw.areaSqMi != null && (
              <div className="flex items-start justify-between gap-4">
                <dt className="text-sm text-gray-500 flex-shrink-0">Area</dt>
                <dd className="text-sm font-medium text-gray-900 text-right">
                  {details.raw.areaSqMi.toLocaleString()} sq mi
                </dd>
              </div>
            )}

            {/* Business model specific variables from breakdown */}
            {details.breakdown &&
              details.breakdown.length > 0 &&
              (() => {
                // Fields already shown above (normalized for comparison)
                const shownFields = new Set([
                  "population",
                  "households",
                  "medianhouseholdincome",
                  "populationdensity",
                  "areasqmi",
                ]);

                // Filter breakdown to exclude already-shown fields
                const additionalVars = details.breakdown.filter((item) => {
                  const normalizedId = item.variableId
                    ?.toLowerCase()
                    .replace(/[_\s]/g, "");
                  const normalizedName = item.variableName
                    ?.toLowerCase()
                    .replace(/[_\s]/g, "");
                  return (
                    !shownFields.has(normalizedId) &&
                    !shownFields.has(normalizedName)
                  );
                });

                return additionalVars.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-start justify-between gap-4"
                  >
                    <dt className="text-sm text-gray-500 flex-shrink-0">
                      {item.variableName}
                    </dt>
                    <dd className="text-sm font-medium text-gray-900 text-right">
                      {/* {typeof item.rawValue === "number"
                        ? item.rawValue % 1 === 0
                          ? item.rawValue.toLocaleString()
                          : item.rawValue.toFixed(1)
                        : item.rawValue} */}
                      {item.displayValue || "--"}
                    </dd>
                  </div>
                ));
              })()}
          </dl>
        </div>
      )}

      {/* Competitors in this geography */}
      {showCompetitorSection && (
        <div className="px-4 py-3 border-t">
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <Users size={14} className="text-teal-600" />
            Competitors: {competitorsForGeo.length}
          </h4>
          {competitorsForGeo.length === 0 ? (
            <p className="text-xs text-gray-400 italic">
              No competitors in this {geographyLabels.singular.toLowerCase()}
            </p>
          ) : (
            <div className="space-y-2">
              {(showAllCompetitors
                ? competitorsForGeo
                : competitorsForGeo.slice(0, 10)
              ).map((comp) => (
                <div
                  key={comp.placeId}
                  className="flex items-start gap-2 py-1.5 border-b border-gray-100 last:border-0"
                >
                  <div
                    className="w-2 h-2 rounded-full bg-teal-600 mt-1.5 flex-shrink-0"
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    {comp.website ? (
                      <a
                        href={comp.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-secondary hover:text-secondary-dark hover:underline inline-flex items-center gap-1"
                      >
                        <span className="truncate">{comp.name}</span>
                        <ExternalLink size={10} className="flex-shrink-0" />
                      </a>
                    ) : (
                      <span className="text-sm font-medium text-gray-800">
                        {comp.name}
                      </span>
                    )}
                    <p className="text-xs text-gray-400">{comp.address}</p>
                  </div>
                </div>
              ))}
              {competitorsForGeo.length > 10 && !showAllCompetitors && (
                <button
                  onClick={() => setShowAllCompetitors(true)}
                  className="text-xs text-secondary hover:text-secondary-dark font-medium mt-1"
                >
                  Show all {competitorsForGeo.length} competitors
                </button>
              )}
              {showAllCompetitors && competitorsForGeo.length > 10 && (
                <button
                  onClick={() => setShowAllCompetitors(false)}
                  className="text-xs text-secondary hover:text-secondary-dark font-medium mt-1"
                >
                  Show fewer
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GeographyDetails;
